import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from '../types'
import { setAdditionalFields, setUser, sessionRequested } from '../user/user'
import { UserFeatures } from '../user/constants'
import { setBiometry, setTheme } from '../settings/settings'
import { Themes } from '../settings/constants'
import { getLang } from '../language/language'
import { getMessage } from '@/hooks/useTranslates'
import { notifyBiometry } from '@/utils/app'
import { runWithSession } from '@/utils/auth/sessionGate'
import { show as showNotify } from '@/components/Modal/UserNotify/utils'

export const createListenerMiddlewareInstance = () => {
  const listenerMiddleware = createListenerMiddleware()

  const startAppListening = listenerMiddleware.startListening.withTypes<
    RootState,
    AppDispatch
  >()

  // Settings gated by subscription features. The subscription arrives
  // asynchronously (undefined at first), so we react to user updates: once it is
  // present and lacks the feature, the setting is reset. Biometry is reset on
  // logout as well — it must not leak into the next session on the same device —
  // and the native app keeps its own flag, so it has to be notified too.
  startAppListening({
    matcher: isAnyOf(setUser, setAdditionalFields),
    effect: (_action, { getState, dispatch }) => {
      const state = getState()
      const subscribe = state.user.user?.subscribe
      if (state.user.user && !subscribe) {
        return
      }
      if (subscribe && !subscribe.features[UserFeatures.CustomThemes] &&
        state.settings.theme !== Themes.light) {
        dispatch(setTheme(Themes.light))
      }
      if (!subscribe?.features[UserFeatures.BiometricSecurity] &&
        state.settings.biometry.enabled) {
        notifyBiometry({ enabled: false })
        dispatch(setBiometry({ enabled: false }))
      }
    }
  })

  // Establish a session by calling `authenticate` whenever a user is set that
  // carries an `accessToken` but no session `token` yet, and once at app
  // startup (via `sessionRequested`) for a user restored from localStorage.
  // We deliberately do NOT match `setAdditionalFields` — that is the action
  // that delivers the `token` and would otherwise re-enter this listener.
  startAppListening({
    matcher: isAnyOf(setUser, sessionRequested),
    effect: async (_action, { getState, dispatch, cancelActiveListeners }) => {
      const user = getState().user.user
      if (!user?.memberId || !user.accessToken || user.token) {
        return
      }
      cancelActiveListeners()
      try {
        // The whole block runs behind the session gate so that requests fired
        // while the session is being established wait for the token.
        await runWithSession(async () => {
          // Dynamic import avoids a load-time cycle:
          // listenerMiddleware -> utils/auth -> @/store -> createAppStore.
          const { auth } = await import('@/utils/auth/auth')
          await auth()
        })
      } catch {
        // Session could not be established — notify and drop the user (logout).
        showNotify({ message: getMessage(getLang(getState()), 'some_went_wrong') })
        dispatch(setUser(null))
      }
    }
  })

  return listenerMiddleware
}
